import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ToolBar } from '@1c/ui';
import { useSearchParams } from 'src/utils/useSearchParams';
import { useNavigate } from 'src/utils/useNavigate';
import {
  postTwoFactorPhoneAuthorizationConfirmAPI,
  postTwoFactorPhoneAuthorizationStartAPI,
} from 'src/serverApi/authorization';
import { AuthorizationContext } from 'src/contexts/authorizationContext';
import routes from 'src/constants/routes';
import ContentLayout from 'src/components/ContentLayout';
import SBPLogo from 'src/components/SBPLogo';
import LinkButton from 'src/components/LinkButton';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import Text from 'src/components/Text';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import styles from './styles.module.scss';

const PhoneConfirm: React.FC = () => {
  const navigate = useNavigate();

  const inputMask = createNumberMask({
    integerLimit: 6,
    prefix: '',
    includeThousandsSeparator: false,
    allowLeadingZeroes: true,
  });

  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const historyBack = searchParams.get('historyBack');
  const authorizationState = useContext(AuthorizationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [confirmTimeout, setConfirmTimeout] = useState(0);
  const [attemptsRemained, setAttemptsRemained] = useState(0);

  const startConfirmTimer = (confirmTimeout: number) => {
    setConfirmTimeout(confirmTimeout);

    const interval = setInterval(() => {
      setConfirmTimeout(prev => {
        if (prev === 0) {
          clearInterval(interval);
          return prev;
        }

        return prev - 1;
      });
    }, 1 * 1000);
  };

  const twoFactorPhoneAuthorizationStart = async () => {
    try {
      setIsLoading(true);
      const sessionConfirmationState = await postTwoFactorPhoneAuthorizationStartAPI('SMS');
      setAttemptsRemained(sessionConfirmationState?.attemptsRemained);
      startConfirmTimer(sessionConfirmationState?.timeoutSeconds);
    } catch (error: any) {
      if (error.type === 'PHONE_PROCESSING_EXCEPTION') {
        toast.error('Не удалось отправить СМС на указанный номер. Проверьте правильность введенного номера телефона или повторите попытку позже');
        return;
      }
      toast.error('Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    twoFactorPhoneAuthorizationStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRepeatSMS = () => {
    twoFactorPhoneAuthorizationStart();
  };

  const confirmAuthorization = async (data: any) => {
    try {
      setIsLoading(true);
      const confirmData = await postTwoFactorPhoneAuthorizationConfirmAPI(data);

      if (confirmData?.status === 'SUCCESS') {
        navigate(returnUrl || routes.agent);
      }

      // при ошибке кода подтверждения бэкенд присылает 200 ответ и в данных объект с status = 'IN_PROGRESS'
      // также уменьшается счетчик attemptsRemained
      // поэтому эта ошибка обрабатывается отдельно, не в блоке catch
      if (confirmData?.status === 'IN_PROGRESS') {
        if (confirmData?.attemptsRemained < attemptsRemained) {
          setAttemptsRemained(confirmData?.attemptsRemained);
          toast.error(
            `Некорректный код подтверждения. Осталось попыток: ${confirmData?.attemptsRemained}`
          );
        }
      }
    } catch (error) {
      const { type } = error as any;
      const messages: { [key: string]: string } = {
        ['CONCURRENT_EXECUTION']:
          'В другом сеансе сейчас работают с этим номером телефона. Необходимо подождать.',
        ['MAX_ATTEMPTS_EXCEEDED']:
          'Превышено количество попыток ввода кода. Необходимо дождаться завершения таймаута.',
        ['NOT_FOUND']: 'Не определен номер телефона, для которого отправлено подтверждение.',
        ['PHONE_PROCESSING_EXCEPTION']: 'неизвестная ошибка у оператора SMS.',
      };
      toast.error(messages[type] || 'Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    confirmAuthorization(values?.code);
  };

  const handleBack = () => {
    if (historyBack === 'true') {
      navigate(-1);
    } else {
      navigate(returnUrl || routes.agent);
    }
  };

  return (
    <ContentLayout size="small">
      <SBPLogo className={styles['sbp-logo']} />
      <Text type="h2" className={styles['title']}>
        Подтверждение пользователя
      </Text>
      <Text type="p" muted>
        В ближайшее время на номер {authorizationState?.userId} придет СМС с кодом.
      </Text>
      <FinalForm
        onSubmit={handleSubmit}
        submitting={isLoading}
        pristine
        initialValues={{ code: undefined }}
      >
        {({ submitting, pristine }: { submitting: any; pristine: any }) => {
          return (
            <div className={styles['form']}>
              {confirmTimeout > 0 ? (
                <FinalField name="code">
                  {({ onChange }: { onChange: any }) => (
                    <InputField
                      label={`Введите код (осталось ${confirmTimeout} сек.)`}
                      onChange={onChange}
                      mask={inputMask}
                      className={styles['input']}
                    />
                  )}
                </FinalField>
              ) : (
                <div className={styles['resend-wrapper']}>
                  <Text type="p" muted>
                    Истекло время для ввода кода
                    <br />
                    <LinkButton
                      onClick={handleRepeatSMS}
                      className={styles['link']}
                    >
                      Отправить СМС повторно
                    </LinkButton>
                  </Text>
                </div>
              )}
              <ToolBar align="center" className={styles['buttons']}>
                <Button variant="text" disabled={submitting} onClick={handleBack}>
                  Назад
                </Button>
                <Button disabled={submitting || pristine} type="submit">
                  Продолжить
                </Button>
              </ToolBar>
            </div>
          );
        }}
      </FinalForm>
    </ContentLayout>
  );
};

export default PhoneConfirm;
